/**
 * Polygon measurement utilities for the Morph engine.
 * Ported from androidx.graphics.shapes.PolygonMeasure.kt
 *
 * Measures the outline of a RoundedPolygon by arc length, normalizing
 * progress values to [0, 1]. Used by Morph to match curves between polygons.
 */

import type { RoundedPolygon } from "../core/rounded-polygon";
import type { Cubic } from "../math/cubic";
import { distance } from "../utils/utils";

/** A Cubic with its start/end outline progress values normalized to [0, 1]. */
export interface MeasuredCubic {
	readonly cubic: Cubic;
	readonly startOutlineProgress: number;
	readonly endOutlineProgress: number;
}

/** Feature info extracted from a measured polygon (for featureMapper). */
export interface MeasuredFeature {
	/** Progress at the midpoint of this feature's central arc */
	readonly progress: number;
	/** Whether this corner is convex */
	readonly convex: boolean;
}

/**
 * A list of MeasuredCubics covering the full outline of a polygon.
 * Provides methods to cut and shift the polygon at a given progress point.
 */
export class MeasuredPolygon {
	private constructor(
		readonly measuredCubics: MeasuredCubic[],
		readonly features: MeasuredFeature[],
	) {}

	get size(): number {
		return this.measuredCubics.length;
	}

	getOrNull(index: number): MeasuredCubic | null {
		return index >= 0 && index < this.measuredCubics.length
			? this.measuredCubics[index]
			: null;
	}

	/**
	 * Cuts the polygon at cutPoint and rotates so that cutPoint becomes progress=0.
	 * Returns a new MeasuredPolygon with adjusted progress values.
	 * @param cutPoint - Progress value [0, 1] at which to cut
	 */
	cutAndShift(cutPoint: number): MeasuredPolygon {
		if (cutPoint <= 0 || cutPoint >= 1) return this;

		const result: MeasuredCubic[] = [];

		// Find which cubic contains the cut point
		let cutCubicIdx = -1;
		for (let i = 0; i < this.measuredCubics.length; i++) {
			if (this.measuredCubics[i].endOutlineProgress >= cutPoint) {
				cutCubicIdx = i;
				break;
			}
		}

		if (cutCubicIdx === -1) cutCubicIdx = this.measuredCubics.length - 1;

		const cutCubic = this.measuredCubics[cutCubicIdx];
		const segLen = cutCubic.endOutlineProgress - cutCubic.startOutlineProgress;
		const t =
			segLen > 0 ? (cutPoint - cutCubic.startOutlineProgress) / segLen : 0;

		// Split the cut cubic
		const [left, right] = cutCubic.cubic.split(t);

		// Build shifted list starting from the right part of the cut cubic
		const shift = (p: number) => {
			let s = p - cutPoint;
			if (s < 0) s += 1;
			return s;
		};

		// Right part of cut cubic
		if (!right.zeroLength()) {
			result.push({
				cubic: right,
				startOutlineProgress: 0,
				endOutlineProgress: shift(cutCubic.endOutlineProgress),
			});
		}

		// Remaining cubics after cut point (wrapping around)
		for (let i = 1; i < this.measuredCubics.length; i++) {
			const idx = (cutCubicIdx + i) % this.measuredCubics.length;
			const mc = this.measuredCubics[idx];
			if (!mc.cubic.zeroLength()) {
				result.push({
					cubic: mc.cubic,
					startOutlineProgress: shift(mc.startOutlineProgress),
					endOutlineProgress: shift(mc.endOutlineProgress),
				});
			}
		}

		// Left part of cut cubic
		if (!left.zeroLength()) {
			result.push({
				cubic: left,
				startOutlineProgress: shift(cutCubic.startOutlineProgress),
				endOutlineProgress: 1,
			});
		}

		return new MeasuredPolygon(result, this.features);
	}

	/**
	 * Cuts a MeasuredCubic at a given outline progress, returning [segment, rest].
	 */
	cutAtProgress(
		measuredCubic: MeasuredCubic,
		progress: number,
	): [MeasuredCubic, MeasuredCubic] {
		const segLen =
			measuredCubic.endOutlineProgress - measuredCubic.startOutlineProgress;
		const t =
			segLen > 0 ? (progress - measuredCubic.startOutlineProgress) / segLen : 0;
		const [left, right] = measuredCubic.cubic.split(
			Math.max(0, Math.min(1, t)),
		);
		return [
			{
				cubic: left,
				startOutlineProgress: measuredCubic.startOutlineProgress,
				endOutlineProgress: progress,
			},
			{
				cubic: right,
				startOutlineProgress: progress,
				endOutlineProgress: measuredCubic.endOutlineProgress,
			},
		];
	}

	/**
	 * Measures a RoundedPolygon's outline by arc length.
	 * Returns a MeasuredPolygon with normalized progress in [0, 1].
	 */
	static measurePolygon(polygon: RoundedPolygon): MeasuredPolygon {
		const cubics = polygon.cubics;

		// Calculate total arc length (approximate by chord lengths)
		let totalLength = 0;
		const lengths: number[] = cubics.map((c) => {
			const len = approximateLength(c);
			totalLength += len;
			return len;
		});

		if (totalLength === 0) {
			return new MeasuredPolygon(
				cubics.map((c) => ({
					cubic: c,
					startOutlineProgress: 0,
					endOutlineProgress: 0,
				})),
				[],
			);
		}

		// Assign normalized progress values
		const measured: MeasuredCubic[] = [];
		let accumulated = 0;
		for (let i = 0; i < cubics.length; i++) {
			const startP = accumulated / totalLength;
			accumulated += lengths[i];
			const endP = accumulated / totalLength;
			measured.push({
				cubic: cubics[i],
				startOutlineProgress: startP,
				endOutlineProgress: endP,
			});
		}

		// Extract feature progress values (midpoint of each corner's central arc)
		const featureList: MeasuredFeature[] = [];
		let cubicIdx = 0;
		for (const feature of polygon.features) {
			if (feature.type === "corner" && feature.cubics.length > 0) {
				// For rounded corners with 3 cubics, the arc is the middle one
				// Find progress at midpoint of corner
				const middleCubicIdx = feature.cubics.length > 1 ? 1 : 0;
				// Map feature cubic back to global cubic index
				const mc =
					measured[Math.min(cubicIdx + middleCubicIdx, measured.length - 1)];
				if (mc) {
					const midProgress =
						(mc.startOutlineProgress + mc.endOutlineProgress) / 2;
					featureList.push({ progress: midProgress, convex: feature.convex });
				}
			}
			cubicIdx += feature.cubics.length;
		}

		return new MeasuredPolygon(measured, featureList);
	}
}

/** Approximate cubic length using the chord from anchor0 to anchor1. */
function approximateLength(cubic: Cubic): number {
	// More accurate: sample a few points
	let len = 0;
	let prevX = cubic.anchor0X,
		prevY = cubic.anchor0Y;
	const steps = 4;
	for (let i = 1; i <= steps; i++) {
		const p = cubic.pointOnCurve(i / steps);
		len += distance(p.x - prevX, p.y - prevY);
		prevX = p.x;
		prevY = p.y;
	}
	return len;
}
